import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Briefcase, Calendar, Receipt, ExternalLink, Building2, CheckCircle2, Clock, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ClientKYCCard from './ClientKYCCard';

const STATUS_STYLES = {
  pending_payment: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Pending Payment', icon: Clock },
  active: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Active', icon: CheckCircle2 },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Cancelled', icon: XCircle },
  expired: { bg: 'bg-slate-500/10', text: 'text-slate-500', label: 'Expired', icon: XCircle },
  payment_failed: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Payment Failed', icon: AlertCircle },
};

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ClientVirtualOfficeView = ({ api, cardBg, cardBorder, navigate }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const res = await api.get('/virtual-office/subscriptions');
      setSubs(res.data || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/virtual-office/subscriptions');
        setSubs(res.data || []);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (subs.length === 0) {
    return (
      <Card className={`${cardBg} ${cardBorder}`}>
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-heading text-xl font-semibold mb-2">No Virtual Office subscription</h3>
          <p className="text-muted-foreground mb-6">Get a premium Bangalore business address, GST registration support, mail handling, and more.</p>
          <Button onClick={() => navigate('/virtual-office')} className="bg-primary hover:bg-primary/90" data-testid="vo-empty-explore">
            Explore Virtual Office Plans
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-vo-view">
      {subs.map((s) => {
        const st = STATUS_STYLES[s.status] || STATUS_STYLES.pending_payment;
        const Icon = st.icon;
        return (
          <Card key={s.id} className={`${cardBg} ${cardBorder}`} data-testid={`client-vo-${s.id}`}>
            <CardContent className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-xl font-bold">{s.tier_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text} flex items-center gap-1 font-medium`}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> {s.venue_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  {s.razorpay_payment_link_url && s.status === 'pending_payment' && (
                    <Button size="sm" onClick={() => window.open(s.razorpay_payment_link_url, '_blank')} data-testid={`client-vo-pay-${s.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Complete Payment
                    </Button>
                  )}
                  {s.zoho_invoice_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/virtual-office/subscriptions/${s.id}/invoice.pdf`, { responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 30000);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Could not load invoice. Please try again.');
                        }
                      }}
                      data-testid={`client-vo-invoice-${s.id}`}
                    >
                      <Receipt className="w-3.5 h-3.5 mr-1" /> Invoice
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Term</p>
                  <p className="font-medium text-sm flex items-center gap-1 mt-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(s.start_date)} → {fmtDate(s.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Annual Amount</p>
                  <p className="font-medium text-primary mt-1">{fmtINR(s.total_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Company</p>
                  <p className="font-medium text-sm mt-1">{s.company_name}</p>
                </div>
              </div>

              {s.status === 'active' && (
                <div className="mt-4 p-3 rounded bg-primary/5 border border-primary/20 text-sm">
                  <p className="font-medium">✨ Subscription active</p>
                  <p className="text-xs text-muted-foreground mt-1">Your business address is live. KYC documents will be requested by our team shortly.</p>
                </div>
              )}
              {s.status === 'pending_payment' && (
                <div className="mt-4 p-3 rounded bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm">
                  <p className="font-medium text-amber-700 dark:text-amber-400">Payment pending</p>
                  <p className="text-xs text-muted-foreground mt-1">Complete payment using the link emailed to you to activate this subscription.</p>
                </div>
              )}
              {s.status === 'cancelled' && s.cancellation_reason && (
                <div className="mt-4 p-3 rounded bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-sm">
                  <p className="font-medium text-red-700 dark:text-red-400">Cancelled · {s.cancellation_reason}</p>
                  {s.refund_amount > 0 && <p className="text-xs text-muted-foreground mt-1">Refund: {fmtINR(s.refund_amount)} processed.</p>}
                </div>
              )}
              {s.status === 'active' && <ClientKYCCard sub={s} api={api} onUpdated={reload} />}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ClientVirtualOfficeView;
